import {
  GEOMETRY_SCHEMA_VERSION,
  type Canvas,
  type SectionKind,
  type SeatGroupKind,
  type VenueMapTopology,
} from '@gatecheckpro/contracts';

/**
 * The editor's working document, and how it relates to what the server sends.
 *
 * Two shapes exist and they are not interchangeable. The server *sends* a view
 * keyed on identifiers — `sectionId`, `seatId`, `seatIdentityId` — because that
 * is what later authority pins. The server *accepts* a document keyed on codes
 * and seat keys, because a whole-document import has to be able to find the
 * same chair again rather than allocate a second one.
 *
 * The editor works in document form throughout. Converting once on load and
 * once after each save keeps a single representation in the editor, and means
 * what is drawn on the canvas is exactly what would be sent — there is no
 * second model that could disagree with the one being saved.
 *
 * Every coordinate here is an integer in the version's logical canvas
 * (ADR 0036). Nothing in this module touches device pixels; scaling to a
 * viewport is the renderer's job and never changes stored geometry.
 */

export function emptyDocument(canvas: Canvas): VenueMapTopology {
  return {
    schemaVersion: GEOMETRY_SCHEMA_VERSION,
    canvas,
    sections: [],
    seats: [],
    seatGroups: [],
    companions: [],
  };
}

// --- Authoring ---------------------------------------------------------------

/**
 * Layout is computed from integers and nothing else.
 *
 * The same parameters produce the same coordinates on any machine, which is
 * what lets a reload assert an identical render rather than a similar one. A
 * generator that reached for the viewport size, a random jitter or a float
 * would break that quietly and only at scale.
 */
export interface RowBlockSpec {
  readonly sectionCode: string;
  readonly rowCount: number;
  readonly seatsPerRow: number;
  readonly originX: number;
  readonly originY: number;
  readonly rowSpacing: number;
  readonly seatSpacing: number;
}

/** Row labels people actually use: A…Z, then AA, AB, and so on. */
export function rowLabel(index: number): string {
  let label = '';
  let n = index;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/**
 * Grows the canvas so it contains everything drawn on it.
 *
 * A new draft starts on the server's default 1,000 x 1,000, which is smaller
 * than any real room. Geometry outside the canvas is not wrong — it saves and
 * reloads intact — but nothing can see it, because the viewport is the canvas
 * and culling correctly leaves it out. So the canvas grows with the room rather
 * than the room being folded to fit a default nobody chose.
 *
 * It only ever grows. Shrinking it when a section is removed would move every
 * remaining seat relative to the viewport for no reason a person asked for.
 */
function covering(
  canvas: Canvas,
  box: { x: number; y: number; width: number; height: number },
): Canvas {
  const padding = 200;
  return {
    width: Math.max(canvas.width, box.x + box.width + padding),
    height: Math.max(canvas.height, box.y + box.height + padding),
  };
}

export function addSection(
  document: VenueMapTopology,
  spec: {
    code: string;
    displayName: string;
    kind: SectionKind;
    x: number;
    y: number;
    width: number;
    height: number;
  },
): VenueMapTopology {
  return {
    ...document,
    canvas: covering(document.canvas, spec),
    sections: [
      ...document.sections,
      {
        code: spec.code,
        displayName: spec.displayName,
        kind: spec.kind,
        displayOrder: document.sections.length,
        geometry: {
          kind: 'RECT',
          x: spec.x,
          y: spec.y,
          width: spec.width,
          height: spec.height,
        },
        rows: [],
        tables: [],
      },
    ],
  };
}

/**
 * Adds rows of seats to a section.
 *
 * Seat keys are `SECTION/ROW/NUMBER`, the convention the contract documents,
 * because that string is what makes a re-import find the same chair instead of
 * inventing a second one.
 */
export function addRowBlock(document: VenueMapTopology, spec: RowBlockSpec): VenueMapTopology {
  const section = document.sections.find((candidate) => candidate.code === spec.sectionCode);
  if (section === undefined) return document;

  const existingRows = section.rows.length;
  const rows = Array.from({ length: spec.rowCount }, (_unused, index) => {
    const code = rowLabel(existingRows + index);
    return {
      code,
      displayName: `Row ${code}`,
      displayOrder: existingRows + index,
      geometry: {
        kind: 'POLYLINE' as const,
        points: [
          { x: spec.originX, y: spec.originY + index * spec.rowSpacing },
          {
            x: spec.originX + (spec.seatsPerRow - 1) * spec.seatSpacing,
            y: spec.originY + index * spec.rowSpacing,
          },
        ],
      },
    };
  });

  const seats = rows.flatMap((row, rowIndex) =>
    Array.from({ length: spec.seatsPerRow }, (_unused, seatIndex) => ({
      seatKey: `${section.code}/${row.code}/${String(seatIndex + 1)}`,
      seatLabel: String(seatIndex + 1),
      sectionCode: section.code,
      rowCode: row.code,
      tableCode: null,
      isAccessible: false,
      hasObstructedView: false,
      displayOrder: rowIndex * spec.seatsPerRow + seatIndex,
      geometry: {
        kind: 'POINT' as const,
        x: spec.originX + seatIndex * spec.seatSpacing,
        y: spec.originY + rowIndex * spec.rowSpacing,
      },
    })),
  );

  return {
    ...document,
    canvas: covering(document.canvas, {
      x: spec.originX,
      y: spec.originY,
      width: (spec.seatsPerRow - 1) * spec.seatSpacing,
      height: (spec.rowCount - 1) * spec.rowSpacing,
    }),
    sections: document.sections.map((candidate) =>
      candidate.code === section.code
        ? { ...candidate, rows: [...candidate.rows, ...rows] }
        : candidate,
    ),
    seats: [...document.seats, ...seats],
  };
}

/** A table, and the chairs around it, placed on a circle by integer steps. */
export function addTable(
  document: VenueMapTopology,
  spec: {
    sectionCode: string;
    code: string;
    displayName: string;
    seatCapacity: number;
    x: number;
    y: number;
    radius: number;
  },
): VenueMapTopology {
  const section = document.sections.find((candidate) => candidate.code === spec.sectionCode);
  if (section === undefined) return document;

  const seats = Array.from({ length: spec.seatCapacity }, (_unused, index) => {
    const angle = (2 * Math.PI * index) / Math.max(1, spec.seatCapacity);
    return {
      seatKey: `${section.code}/${spec.code}/${String(index + 1)}`,
      seatLabel: String(index + 1),
      sectionCode: section.code,
      rowCode: null,
      tableCode: spec.code,
      isAccessible: false,
      hasObstructedView: false,
      displayOrder: index,
      geometry: {
        kind: 'POINT' as const,
        // Rounded, because the canvas is an integer space. Chairs around a
        // table land on whole units and stay there across every client.
        x: Math.round(spec.x + Math.cos(angle) * (spec.radius + 40)),
        y: Math.round(spec.y + Math.sin(angle) * (spec.radius + 40)),
      },
    };
  });

  return {
    ...document,
    canvas: covering(document.canvas, {
      x: spec.x - spec.radius - 40,
      y: spec.y - spec.radius - 40,
      width: (spec.radius + 40) * 2,
      height: (spec.radius + 40) * 2,
    }),
    sections: document.sections.map((candidate) =>
      candidate.code === section.code
        ? {
            ...candidate,
            tables: [
              ...candidate.tables,
              {
                code: spec.code,
                displayName: spec.displayName,
                seatCapacity: spec.seatCapacity,
                displayOrder: candidate.tables.length,
                geometry: { kind: 'CIRCLE' as const, x: spec.x, y: spec.y, radius: spec.radius },
              },
            ],
          }
        : candidate,
    ),
    seats: [...document.seats, ...seats],
  };
}

export function addSeatGroup(
  document: VenueMapTopology,
  spec: { code: string; displayName: string; kind: SeatGroupKind; seatKeys: readonly string[] },
): VenueMapTopology {
  if (spec.seatKeys.length === 0) return document;
  return {
    ...document,
    seatGroups: [
      ...document.seatGroups,
      {
        code: spec.code,
        displayName: spec.displayName,
        kind: spec.kind,
        displayOrder: document.seatGroups.length,
        geometry: null,
        seatKeys: [...spec.seatKeys],
      },
    ],
  };
}

/** Removes a section, the chairs in it, and any grouping left holding nothing. */
export function removeSection(document: VenueMapTopology, code: string): VenueMapTopology {
  const seats = document.seats.filter((seat) => seat.sectionCode !== code);
  const surviving = new Set(seats.map((seat) => seat.seatKey));
  return {
    ...document,
    sections: document.sections.filter((section) => section.code !== code),
    seats,
    seatGroups: document.seatGroups
      .map((group) => ({ ...group, seatKeys: group.seatKeys.filter((key) => surviving.has(key)) }))
      .filter((group) => group.seatKeys.length > 0),
  };
}

export function seatCountOf(document: VenueMapTopology): number {
  return document.seats.length;
}
